
import { trackEvent } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { GroupByTypes } from '@padlet/arvo'
import { subscribeUpdateBodySizeCss, unsubscribeUpdateBodySizeCss } from 'bits/body_sizing'
import {
  PasteType,
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
} from 'bits/clipboard'
import { isActiveElementAnInputField, isEventOnAnInputField } from 'bits/dom'
import window from 'bits/global'
import loadMapApi from 'bits/map_api_loader'
import { PiniaMapAction, PiniaMapState } from 'bits/pinia'
import { Action, Component, Getter, State, Vue, Watch, defineAsyncComponent, namespace, stableSync } from 'bits/vue'
import { SEARCH_BAR_DEFAULT_TOP, useSurfaceStore } from 'pinia/surface'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import tinykeys from 'tinykeys'
import type { Id } from 'types'
import SurfaceHeader from 'vuecomponents/SurfaceHeader.vue'
import SurfacePosts from 'vuecomponents/surface_posts.vue'
import DoubleClickToPostMixin from 'vuemixins/double_click_to_post_mixin'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import type { RootState } from 'vuexstore/surface/types'

const browserStore = namespace('browser')
const postStore = namespace('post')
const reactionStore = namespace('reaction')
const commentStore = namespace('comment')
const snackbarsStore = namespace('snackbars')
const mapStore = namespace('map')
const draftStore = namespace('draft')
const composerModalStore = namespace('composerModal')

@Component({
  components: {
    SurfaceHeader,
    SurfacePosts,
    SurfaceActionBar: defineAsyncComponent(() => import('vuecomponents/SurfaceActionBar.vue')),
    SurfaceContainerGridOld: defineAsyncComponent(() => import('vuecomponents/surface_container_grid.vue')),
    SurfaceContainerGrid: defineAsyncComponent(() => import('vuecomponents/SurfaceContainerGrid.vue')),
    SurfaceContainerMap: defineAsyncComponent(() => import('vuecomponents/surface_container_map.vue')),
    SurfaceContainerTable: defineAsyncComponent(() => import('vuecomponents/surface_container_table.vue')),
    SurfaceContainerTimelineOld: defineAsyncComponent(() => import('vuecomponents/surface_container_timeline.vue')),
    SurfaceContainerTimeline: defineAsyncComponent(() => import('vuecomponents/SurfaceContainerTimeline.vue')),
    SurfaceContainerMatrix: defineAsyncComponent(() => import('vuecomponents/SurfaceContainerMatrix.vue')),
    SurfaceContainerStream: defineAsyncComponent(() => import('vuecomponents/SurfaceContainerStream.vue')),
    SurfaceContainerCanvas: defineAsyncComponent(() => import('vuecomponents/SurfaceContainerCanvas.vue')),
    SurfaceAddPost: defineAsyncComponent(() => import('vuecomponents/SurfaceAddPost.vue')),
    SurfacePanels: defineAsyncComponent(() => import('vuecomponents/surface_panels.vue')),
  },
  mixins: [DoubleClickToPostMixin, DropToPostMixin],
})
export default class SurfaceContainer extends Vue {
  GroupByTypes = GroupByTypes
  isMapApiLoaded = false
  removeKeyboardShortcutsListener = (): void => {}

  @State isEmbedded
  @State isMobile
  @State isOnline
  @State((state: RootState) => state.constants.googleApiKeyForMap)
  googleApiKeyForMap

  @State((state: RootState) => state.constants.currentCountryCode)
  currentCountryCode

  @Getter amIOwner
  @Getter backgroundLuminance
  @Getter dir
  @Getter format
  @Getter isOverlayVisible
  @Getter isApp
  @Getter isCanvas
  @Getter isGrid
  @Getter isMap
  @Getter isPostable
  @Getter isReadOnly
  @Getter isScreenshotMode
  @Getter isShelf
  @Getter isStream
  @Getter isSubscriptionPaused
  @Getter isTable
  @Getter isTimeline
  @Getter isTimelineV1
  @Getter isTimelineV2
  @Getter isMatrix
  @Getter isDesktopLayout
  @Getter xHeader
  @Getter isMobileLayout
  @Getter canUseSections!: boolean
  @Getter headerHeight
  @Getter headerColor!: string

  @Action refresh
  @Action resizeHeader

  @browserStore.Getter isSmallerThanTabletLandscape!: boolean

  @postStore.Getter postBeingEditedCid
  @postStore.Action createPost
  @postStore.Action startNewPost
  @PiniaMapState(useSurfaceWishArrangementStore) wishGroupBy!: GroupByTypes

  @reactionStore.Getter reactionIdsByPost!: Record<Id, Id[]>
  @commentStore.Getter commentIdsByPost!: Record<Id, Id[]>

  @snackbarsStore.Getter xSnackbar
  @snackbarsStore.Action setSnackbar

  @mapStore.Action startPickingLocation
  @draftStore.Action startNewDraft
  @composerModalStore.Action('dock') dockComposerModal!: () => void

  @PiniaMapState(useSurfacePermissionsStore) amIRegistered!: boolean
  @PiniaMapAction(useSurfaceDraftsStore) fetchPostDrafts!: () => Promise<void>

  // From mixins
  enableDoubleClickToPost!: (wallContainer: Element, wishList?: Element) => void // from DoubleClickToPostMixin
  disableDoubleClickToPost!: (wallContainer: Element) => void // from DoubleClickToPostMixin
  enableDropToPost!: (wallContainer: Element, callback?: (NewPost) => void) => void // from DropToPostMixin
  disableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin

  $refs!: {
    wallContainer: HTMLDivElement
    surfaceHeader: SurfaceHeader
  }

  @Watch('isOnline')
  requestAutoRefresh(): void {
    this.refresh({ isAutoRefresh: true })
  }

  @Watch('isPostable')
  togglePostHandlers(newValue): void {
    if (newValue) {
      this.enableNewPostHandlers()
    } else {
      this.disableNewPostHandlers()
    }
  }

  @Watch('format')
  loadNewFormat(newValue, oldValue): void {
    if (newValue !== oldValue) {
      // Allow native bridge messages to flush before reloading
      this.$nextTick(() => window.location.reload())
    }
  }

  reactionIdsByPostId: Record<Id, Id[]> = {}

  @Watch('reactionIdsByPost', { immediate: true })
  handleReactionIdsByPostChange(newValue: Record<Id, Id[]>): void {
    stableSync(this.reactionIdsByPostId, newValue)
  }

  commentIdsByPostId: Record<Id, Id[]> = {}

  @Watch('commentIdsByPost', { immediate: true })
  handleCommentIdsByPostChange(newValue: Record<Id, Id[]>): void {
    stableSync(this.commentIdsByPostId, newValue)
  }

  // START LIFECYCLE HOOKS
  created() {
    if (isAppUsing('postDrafts') && this.amIRegistered) {
      // We initiate post draft fetch from this container:
      // 1. It fetches earlier in the app's lifecycle
      // 2. It's guaranteed to be rendered so the fetch always happens.
      //   This allows us to for eg post drafts to the app, where child components may not be rendered.
      this.fetchPostDrafts()
    }
  }

  async mounted(): Promise<void> {
    this.refresh({ isAutoRefresh: false })

    // Ensure new posts are loaded. Realtime might be disconnected when invisible, e.g. on mobile.
    document.addEventListener('visibilitychange', this.refreshContentIfVisible)

    if (this.isMap) {
      await loadMapApi(this.googleApiKeyForMap, {
        region: this.currentCountryCode,
      })
      this.isMapApiLoaded = true
    }
    this.$nextTick(() => {
      if (this.isPostable) {
        this.enableNewPostHandlers()
      }
    })
    if (!this.xHeader) {
      // mobile app with native header
      this.resizeHeader({ height: 0 })
    }

    // Ensure that export as image captures all posts
    if (this.isScreenshotMode && ((this.isGrid && this.wishGroupBy === GroupByTypes.None) || this.isCanvas)) {
      const hookToAllStoreChanges = ((store) => store.subscribe.bind(store))(this.$store)
      const events = ['load', 'beethoven-load', 'error', 'beethoven-error', hookToAllStoreChanges]
      const extraMarginInPx = 200
      subscribeUpdateBodySizeCss('wish', events, extraMarginInPx)
    }
    if (this.isSubscriptionPaused && this.amIOwner) {
      const snackbarData: {
        message?
        persist?
        showCloseButton?
        actionText?
        actionUrl?
      } = {
        message: this.__('Your subscription is paused. Your padlets are read-only until it resumes.'),
        persist: true,
        showCloseButton: true,
      }
      if (!this.isApp) {
        snackbarData.actionText = this.__('Go to Settings')
        snackbarData.actionUrl = '/dashboard/settings/billing'
      }
      this.setSnackbar(snackbarData)
    }

    const shortcuts = {
      KeyC: this.handleOnKeydownC,
      '$mod+KeyF': this.handleOnKeydownModF,
    }
    this.removeKeyboardShortcutsListener = tinykeys(window, shortcuts)
  }

  beforeDestroy(): void {
    this.disableNewPostHandlers()
    unsubscribeUpdateBodySizeCss()
    this.removeKeyboardShortcutsListener()
    document.removeEventListener('visibilitychange', this.refreshContentIfVisible)
  }
  // END LIFECYCLE HOOKS

  enableNewPostHandlers(): void {
    window.addEventListener('paste', this.handleOnPaste as (e: Event) => void)
    if (this.isShelf || this.isGrid || this.isTable || this.isMap || this.isTimeline) return
    if (!this.isMobile && !this.isStream && !this.isCanvas) this.enableDoubleClickToPost(this.$el)
    this.enableDropToPost(this.$el)
  }

  disableNewPostHandlers(): void {
    this.disableDoubleClickToPost(this.$el)
    this.disableDropToPost(this.$el)
    window.removeEventListener('paste', this.handleOnPaste as (e: Event) => void)
  }

  refreshContentIfVisible() {
    if (document.visibilityState === 'visible') {
      this.refresh({ isAutoRefresh: true })
    }
  }

  handleOnPaste(event: ClipboardEvent): void {
    event.stopPropagation()
    if (!this.isPostable) return

    if (isEventOnAnInputField(event)) {
      return
    }

    const data = extractPostAttributesArrayFromClipboardData(event.clipboardData)
    if (!data || !data.length) return

    // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
    // we want to treat them as text
    const isRichTextPastedAsFile =
      event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

    // Create a post with attachment without filename
    // when PasteType is File
    const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)

    if (attachment && !isRichTextPastedAsFile) {
      this.startNewDraft({
        attributes: attachment.postAttributes,
        shouldStartEditing: true,
      })
      this.dockComposerModal()

      trackEvent('Posts', `Added draft by pasting ${attachment.pasteType}`)
      return
    }

    // Create a new post when PasteType is Link or Text or PostContentHash
    const dataWithoutFile = data.filter((datum) => {
      return datum?.pasteType !== PasteType.File
    })

    this.startNewDraft({
      attributes: dataWithoutFile[0].postAttributes,
      shouldStartEditing: true,
    })
    this.dockComposerModal()
    trackEvent('Posts', `Added draft by pasting ${dataWithoutFile[0].pasteType}`)
  }

  handleOnKeydownC(event: KeyboardEvent): void {
    if (!this.isPostable) return
    if (this.postBeingEditedCid) return
    if (this.isOverlayVisible) return
    if (isActiveElementAnInputField()) return
    event.preventDefault()
    if (this.isMap) {
      this.startPickingLocation()
    } else {
      this.startNewPost()
    }
  }

  handleOnKeydownModF(event: KeyboardEvent): void {
    if (this.postBeingEditedCid) return
    if (this.isOverlayVisible) return
    if (isActiveElementAnInputField()) return
    event.preventDefault()
    // Couldn't get Vue ref to work so we will use direct DOM APIs here.
    // TODO: Use Vue ref instead.
    document.getElementById('surface-search-bar')?.querySelector('input')?.select()
    useSurfaceStore().updateSearchBarTop(SEARCH_BAR_DEFAULT_TOP)
    trackEvent('Surface search', 'Used Ctrl F to search')
  }

  headerBackgroundTop = 0
  pageScrollHandlerTicking = false
  @PiniaMapState(useSurfaceStore) isSearchBarInEditMode!: boolean

  @Watch('isSearchBarInEditMode')
  handlePageScroll(): void {
    if (!this.pageScrollHandlerTicking && !this.isMap) {
      window.requestAnimationFrame(() => {
        this.pageScrollHandlerTicking = false

        // Set header underneath background top position to hide it when the page scrolls the header and wishlist away
        const wallContainerEl = this.$refs.wallContainer || document.querySelector('#wall-container')
        if (wallContainerEl != null) {
          this.headerBackgroundTop = -wallContainerEl.scrollTop
        }

        // Search bar is 16px from the top
        // When in edit mode, we keep it fixed so users can scroll to see the posts while searching
        const searchBarTop = SEARCH_BAR_DEFAULT_TOP + (this.isSearchBarInEditMode ? 0 : this.headerBackgroundTop)
        useSurfaceStore().updateSearchBarTop(searchBarTop)
      })

      this.pageScrollHandlerTicking = true
    }
  }
}
